/**
 * TutorialManager.jsx - Interactive tutorial card system
 */

import React, { useState, useEffect } from 'react';
import { TUTORIALS, TUTORIAL_CONFIG, getTutorial } from '../data/tutorials';
import '../styles/tutorial.css';

export function TutorialManager({ currentTutorial, onDismiss, gameState }) {
  const [isVisible, setIsVisible] = useState(false);
  const [cardPosition, setCardPosition] = useState({ top: '50%', left: '50%' });
  const [arrowDirection, setArrowDirection] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const tutorial = currentTutorial ? getTutorial(currentTutorial) : null;

  // Helper to render text with bold markdown
  const renderText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Reset page when tutorial changes
  useEffect(() => {
    setCurrentPage(0);
  }, [currentTutorial]);

  // Show tutorial card when tutorial changes
  useEffect(() => {
    if (!tutorial) {
      setIsVisible(false);
      return;
    }

    // Fade in animation
    setTimeout(() => setIsVisible(true), 50);

    // Center the card
    centerCard();
  }, [tutorial]);

  const centerCard = () => {
    setCardPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
    setArrowDirection(null);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss(tutorial.id);
    }, TUTORIAL_CONFIG.fadeOutDuration);
  };

  if (!tutorial) return null;

  return (
    <>
      {/* Tutorial Card */}
      <div
        className={`tutorial-card ${isVisible ? 'visible' : ''} ${arrowDirection ? `arrow-${arrowDirection}` : ''}`}
        style={cardPosition}
      >
        <div className="tutorial-header">
          <h3 className="tutorial-title">{tutorial.title}</h3>
          <button
            className="tutorial-close"
            onClick={handleDismiss}
            aria-label="Dismiss tutorial"
          >
            ✕
          </button>
        </div>

        <div className="tutorial-body">
          {tutorial.pages ? (
            <>
              <p className="tutorial-description">{renderText(tutorial.pages[currentPage])}</p>
              {tutorial.pages.length > 1 && (
                <div className="tutorial-page-indicator">
                  Page {currentPage + 1} of {tutorial.pages.length}
                </div>
              )}
            </>
          ) : (
            <p className="tutorial-description">{renderText(tutorial.description)}</p>
          )}
        </div>

        <div className="tutorial-footer">
          {tutorial.pages && tutorial.pages.length > 1 ? (
            <>
              {currentPage > 0 && (
                <button className="tutorial-btn secondary" onClick={() => setCurrentPage(currentPage - 1)}>
                  ← Previous
                </button>
              )}
              {currentPage < tutorial.pages.length - 1 ? (
                <button className="tutorial-btn primary" onClick={() => setCurrentPage(currentPage + 1)}>
                  Next →
                </button>
              ) : (
                <button className="tutorial-btn primary" onClick={handleDismiss}>
                  Got it! 👍
                </button>
              )}
            </>
          ) : (
            <button className="tutorial-btn primary" onClick={handleDismiss}>
              Got it! 👍
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default TutorialManager;
